<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import SectionCard from '../lib/ui/SectionCard.svelte';
  import StatCard from '../lib/ui/StatCard.svelte';
  import CopyButton from '../lib/ui/CopyButton.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import PlanBadge from '../lib/ui/PlanBadge.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    parseTs,
    type AdminUser,
  } from '../lib/admin';
  import { ensureCommunities } from '../lib/community.svelte';
  import { } from '@api';
import { fetchMyAffiliate } from '../lib/session';
import type { MyAffiliate } from '../lib/session';
import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fmtUsd, fmtRate, referralLink } from '../lib/affiliates';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let summary = $state<MyAffiliate | null>(null);

  /**
   * ONE round trip, where this page cost three (doc 02 W11).
   *
   * The chain was `GET /admin/me` → `GET /admin/communities` → `GET
   * /admin/affiliate/summary`, and the comment on the middle leg was candid about
   * why: the `await` existed for the SHELL — the sidebar switcher and the trial
   * banner read the community list — and the summary was placed after it by code
   * order, not by dependency. The summary depends on neither. Both now go out
   * together, and the community list arrives with the account in the bootstrap.
   *
   * THE SUMMARY IS `GET /v1/me/affiliate` NOW, and that swap fixed a live 500 rather
   * than merely moving a path: `GET /admin/affiliate/summary` rebinds the imported
   * `holder_flags` FUNCTION to a dict and then calls it (`api/affiliates.py:151,159`),
   * so it raises `TypeError` for any affiliate who has at least one referral — the
   * only affiliates this page is for. The v1 route calls
   * `domain.affiliates.creator_summary`, which is where that assembly lives now, and
   * carries the three blocks (`referrals[]`, `payouts[]`, `paying_community_count`)
   * that kept the page on the legacy route until this commit.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    let pageErr: unknown = null;
    const pending = fetchMyAffiliate().catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      // AdminShell reads the community list (sidebar switcher + trial banner). Free
      // now — it is a view over the bootstrap that has already landed.
      await ensureCommunities();
      const loaded = await pending;
      if (loaded) summary = loaded;
      else if (pageErr) throw pageErr;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }
  void init();

  // --- derived view state (safe against a still-loading summary) --------------
  const code = $derived(summary?.code ?? '');
  const rateLabel = $derived(summary ? fmtRate(summary.rate_bps) : '');
  const link = $derived(referralLink(code));
  // Counted server-side now (`domain.affiliates.PAYING_PLAN_STATUSES`), where this
  // used to be a reduce over every referral's communities in the browser.
  const payingCommunities = $derived(summary?.paying_community_count ?? 0);

  // "How it works" is rendered straight from the program constants, so the rules
  // never drift from the backend's actual cookie window / hold / rate.
  const steps = $derived.by((): string[] => {
    const p = summary?.program;
    if (!p) return [];
    return [
      'Share your referral link anywhere you like.',
      `Anyone who clicks it is credited to you for ${p.cookie_days} days.`,
      'They sign up and add their Skool community.',
      `You earn ${rateLabel} of every invoice, on every community they bring — for as long as they pay.`,
      `Each commission holds for ${p.hold_days} days, then becomes payable.`,
      'We send payouts manually for now — your history shows below.',
    ];
  });

  // `parseTs`, never `new Date` — a tz-less instant is read as LOCAL time, which
  // shifts a date by the viewer's offset (lib/admin.ts:147).
  const fmtDate = (iso: string | null | undefined): string =>
    iso
      ? parseTs(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : '—';
</script>

{#snippet howItWorks(items: string[])}
  <SectionCard title="How it works">
    <ol class="space-y-3">
      {#each items as step, i (i)}
        <li class="flex gap-3">
          <span class="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/10 font-mono text-xs font-bold text-primary">
            {i + 1}
          </span>
          <span class="pt-0.5 text-sm text-base-content/80">{step}</span>
        </li>
      {/each}
    </ol>
  </SectionCard>
{/snippet}

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else if summary}
    <PageContainer>
      <!-- Reached from Preferences (/admin/preferences/affiliates), which is what the
           eyebrow says now. "Sideguide AI ·" was a prefix borrowed from a page in a
           section this one has never been in. -->
      <PageHeader section="Preferences" title="Affiliate program" />
      {#if summary.is_affiliate && code}
        <p class="max-w-xl text-sm text-base-content/72">
          You earn {rateLabel} of every payment, for as long as they stay.
        </p>

        {#if summary.disabled}
          <div class="mt-5 rounded-field border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-warning">
            Your affiliate code is paused — new referrals won't be tracked right now. Contact us to reactivate it.
          </div>
        {/if}

        <!-- Referral-link hero -->
        <section class="mt-6">
          <SectionCard title="Your referral link">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
              <code class="flex-1 truncate rounded-field border hair bg-base-200/50 px-3 py-2.5 font-mono text-sm">{link}</code>
              <CopyButton value={link} label="Copy link"
                          class="btn-primary btn-sm shrink-0" />
            </div>
            <p class="mt-3 text-sm text-base-content/72">
              You earn {rateLabel} of every payment, for as long as they stay.
            </p>
          </SectionCard>
        </section>

        <!-- Metric tiles -->
        <div class="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard label="Referred signups" value={String(summary.referred_count)} />
          <StatCard label="Paying communities" value={String(payingCommunities)} tone="success" />
          <StatCard redact label="Pending" value={fmtUsd(summary.totals.pending_cents)} sub="in the hold window" />
          <StatCard redact label="Payable" value={fmtUsd(summary.totals.payable_cents)} tone="success" sub="cleared the hold" />
          <StatCard redact label="Paid to date" value={fmtUsd(summary.totals.paid_cents)} />
        </div>

        <!-- How it works -->
        <div class="mt-6">{@render howItWorks(steps)}</div>

        <!-- The proof: one row per referral -->
        <section class="mt-6">
          <SectionCard title="Your referrals">
            {#if summary.referrals.length === 0}
              <StateBlock state="empty" message="No referrals yet" hint="Share your link to start earning." compact />
            {:else}
              <div class="overflow-x-auto">
                <table class="table table-sm">
                  <thead>
                    <tr class="text-[0.65rem] uppercase tracking-wide text-base-content/65">
                      <th>Member</th>
                      <th>Joined</th>
                      <th>Communities</th>
                      <th class="text-right">Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each summary.referrals as r (r.user_id)}
                      <tr>
                        <td>
                          <div class="flex items-center gap-2">
                            <Avatar name={r.name} size={26} />
                            <span class="redact text-sm font-medium">{r.name}</span>
                          </div>
                        </td>
                        <td class="whitespace-nowrap text-xs text-base-content/72">{fmtDate(r.joined_at)}</td>
                        <td>
                          {#if r.communities.length === 0}
                            <span class="text-xs text-base-content/55">—</span>
                          {:else}
                            <div class="flex flex-wrap items-center gap-1.5">
                              {#each r.communities as s (s.id)}
                                <span class="inline-flex items-center gap-1.5 rounded-field bg-base-200/60 py-0.5 pl-2 pr-1">
                                  <span class="redact text-xs">{s.name}</span>
                                  <PlanBadge status={s.plan_status} />
                                </span>
                              {/each}
                            </div>
                          {/if}
                        </td>
                        <td class="redact whitespace-nowrap text-right font-mono text-sm tabular-nums">{fmtUsd(r.earned_cents)}</td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          </SectionCard>
        </section>

        <!-- Payout history (only when there is any) -->
        {#if summary.payouts.length > 0}
          <section class="mt-6">
            <SectionCard title="Payout history">
              <ul class="divide-y divide-base-content/5">
                {#each summary.payouts as p, i (i)}
                  <li class="flex items-center justify-between py-2.5 text-sm">
                    <span class="text-base-content/72">{fmtDate(p.paid_out_at)}</span>
                    <span class="redact font-mono tabular-nums">{fmtUsd(p.amount_cents)}</span>
                  </li>
                {/each}
              </ul>
            </SectionCard>
          </section>
        {/if}
      {:else}
        <!-- Non-affiliate: the explainer + a quiet invite-only note. -->
        <p class="mt-2 max-w-xl text-sm text-base-content/72">
          Refer other Skool owners to Sideguide and earn a recurring share of what they pay.
        </p>
        <div class="mt-6">{@render howItWorks(steps)}</div>
        <p class="mt-6 rounded-field border hair bg-base-100/60 px-4 py-3 text-sm text-base-content/70">
          The affiliate program is invite-only for now — contact us to join.
        </p>
      {/if}
    </PageContainer>
  {/if}
</AdminShell>
